"use client";

import CircularProgress from "@/components-shared/CircularProgress";
import PhoneInput from "@/components-shared/phoneInput";
import { Button } from "@/components-shared/ui/button";
import { FloatInput } from "@/components-shared/ui/input";
import { FloatTextarea } from "@/components-shared/ui/textarea";
import ehjzleeInstance from "@/lib/ehjzleeInstance";
import { RootState } from "@/redux/store";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslations } from "next-intl";
import React, { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { isValidPhoneNumber } from "react-phone-number-input";
import { useSelector } from "react-redux";
import { toast } from "sonner";
import * as yup from "yup";

const ContactForm = () => {
  const t = useTranslations();
  const { isLoggedIn } = useSelector((state: RootState) => state.login);
  const user = useSelector((state: RootState) => state.user);

  const { control, handleSubmit, formState, reset } = useForm({
    mode: "all",
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      subject: "",
      details: "",
    },
    resolver: yupResolver(
      yup.object({
        firstName: yup.string().required(t("form.requiredFirstName")),
        lastName: yup.string().required(t("form.requiredLastName")),
        email: yup
          .string()
          .email(t("form.invalidEmail"))
          .required(t("form.requiredEmail")),
        phone: yup
          .string()
          .required(t("form.requiredPhone"))
          .test({
            test: (value) => isValidPhoneNumber(value),
            message: t("form.invalidPhone"),
          }),
        subject: yup.string().required(t("form.requiredSubject")),
        details: yup.string().required(t("form.requiredConfirmDetails")),
      })
    ),
  });

  const onSubmit = async (values: any) => {
    try {
      await ehjzleeInstance.post("contact-requests", values);
      toast.success(t("contactForm.success"));
    } catch (error) {
      toast.error(t("contactForm.error"));
      console.log(error);
    } finally {
      reset({
        phone: user?.mobile || "",
        subject: "",
        details: "",
      });
    }
  };

  useEffect(() => {
    if (user?.id)
      reset({
        phone: user?.mobile!,
        firstName: user?.firstName!,
        lastName: user?.lastName!,
        email: user?.email || "",
        details: "",
        subject: "",
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="sm:col-span-3 shadow-main p-5 sm:p-12 flex flex-col gap-3 sm:grid sm:grid-cols-2 sm:gap-5 rounded-2xl"
    >
      <div className="col-span-full justify-center flex items-center border rounded-2xl">
        {t("contactForm.liveChat")}
        <a
          href="https://salesiq.zohopublic.com/signaturesupport.ls?widgetcode=siqf983c0d3b3b47fd05e7248a20a94220d0a21fe2287a5791a684d431b43ec119f&e=siq216a2a412716605f325ba9df057c79763d19fbc16439dc0d3e11ea2508d66f95"
          target="_blank"
          rel="noopener noreferrer"
          className="w-36"
        >
          <img src="https://salesiq.zohopublic.com/visitor/v2/channels/emailsignature/sticker?widgetcode=siqf983c0d3b3b47fd05e7248a20a94220d0a21fe2287a5791a684d431b43ec119f&enc_agent_mail=siq216a2a412716605f325ba9df057c79763d19fbc16439dc0d3e11ea2508d66f95" />
        </a>
      </div>
      <Controller
        name="firstName"
        control={control}
        render={({ field, fieldState }) => (
          <FloatInput
            {...field}
            placeholder={t("contactForm.firstName")}
            required
            disabled={isLoggedIn && !!user?.firstName}
            hasError={fieldState.invalid}
            errorMessage={fieldState.error?.message}
          />
        )}
      />
      <Controller
        name="lastName"
        control={control}
        render={({ field, fieldState }) => (
          <FloatInput
            {...field}
            placeholder={t("contactForm.lastName")}
            required
            disabled={isLoggedIn && !!user?.lastName}
            hasError={fieldState.invalid}
            errorMessage={fieldState.error?.message}
          />
        )}
      />
      <PhoneInput
        disabled={isLoggedIn && !!user?.mobile}
        control={control}
        name="phone"
      />
      <Controller
        name="email"
        control={control}
        render={({ field, fieldState }) => (
          <FloatInput
            {...field}
            required
            type="email"
            disabled={isLoggedIn && !!user?.email}
            placeholder={t("contactForm.email")}
            hasError={fieldState.invalid}
            errorMessage={fieldState.error?.message}
          />
        )}
      />
      <Controller
        name="subject"
        control={control}
        render={({ field, fieldState }) => (
          <div className="col-span-2 sm:mt-[18px]">
            <FloatInput
              required
              {...field}
              placeholder={t("contactForm.subject")}
              hasError={fieldState.invalid}
              errorMessage={fieldState.error?.message}
            />
          </div>
        )}
      />
      <Controller
        name="details"
        control={control}
        render={({ field, fieldState }) => (
          <FloatTextarea
            required
            {...field}
            placeholder={t("contactForm.inquiryDetails")}
            className="col-span-2 sm:mt-[18px]"
            hasError={fieldState.invalid}
            errorMessage={fieldState.error?.message}
          />
        )}
      />
      <Button
        disabled={formState.isSubmitting || !formState.isValid}
        className="col-span-2 bg-green-main hover:bg-green-400"
      >
        {formState.isSubmitting ? (
          <CircularProgress />
        ) : (
          t("contactForm.sendInquiry")
        )}
      </Button>
    </form>
  );
};

export default ContactForm;
